// FEAT-29 — the candidate's OWN test cases on a coding question.
//
// A case is an input and, optionally, the output the CANDIDATE expects. It
// runs with "Run tests", next to the question's example tests:
//   - Python / JavaScript / TypeScript / SQL: in the tab, by the same engines
//     and the same comparison as the example tests (`runOwnTestsInBrowser`);
//   - the languages that run on our servers: in the same job as the example
//     tests (`ownTestsPayload` → `/run`), one Run against the throttle.
//
// What it can never be:
//   - an answer oracle. There is no reference solution anywhere, so a case
//     shows the candidate's own output — "passed" only ever means "equal to
//     what YOU typed as expected".
//   - graded, or saved with the answer. The cases live in sessionStorage (the
//     lifetime of the rest of a candidate's tab state) and their results are
//     kept apart from the example tests', which ARE saved as the answer's
//     visible test results.
//
// All wording is a placeholder (Emilia's).
import { useCallback, useEffect, useState } from "react";

import { runUserCodeAgainstTest, type Language } from "@/lib/runtimes";
import { authoringFor } from "@/lib/runtimes/authoring";
import { isJsContract, type QuestionContract } from "@/lib/runtimes/registry";

export const MAX_OWN_TESTS = 5;
/** Per field — the server's own limit for a case sent to the runner. */
export const MAX_OWN_TEST_CHARS = 16_000;

export interface OwnTest {
  /** A stable row key; never sent anywhere. */
  id: string;
  input: string;
  /** "" = nothing expected: the Run just shows the output. */
  expected: string;
}

export interface OwnTestResult {
  /** The input this result ran with (the row may have been edited since). */
  input: string;
  /** Against the candidate's own expected output; null = it ran and nothing
   *  was expected, or it was not run at all (`note`). */
  passed: boolean | null;
  output?: string;
  error?: string;
  /** Why the case was not run. */
  note?: string;
}

let nextId = 0;
export function newOwnTest(): OwnTest {
  nextId += 1;
  return { id: `own-${Date.now().toString(36)}-${nextId}`, input: "", expected: "" };
}

// ── storage ────────────────────────────────────────────────────────────────

const STORAGE_PREFIX = "ownTests:v1:";

/** Whatever was stored, as cases the page can take. PURE. */
export function sanitizeOwnTests(raw: unknown): OwnTest[] {
  if (!Array.isArray(raw)) return [];
  const out: OwnTest[] = [];
  for (const item of raw) {
    if (out.length >= MAX_OWN_TESTS) break;
    if (!item || typeof item !== "object") continue;
    const src = item as Record<string, unknown>;
    if (typeof src.input !== "string" || typeof src.expected !== "string") continue;
    out.push({
      id: typeof src.id === "string" && src.id ? src.id : newOwnTest().id,
      input: src.input.slice(0, MAX_OWN_TEST_CHARS),
      expected: src.expected.slice(0, MAX_OWN_TEST_CHARS),
    });
  }
  return out;
}

function load(key: string | null): OwnTest[] {
  if (key === null) return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    return raw ? sanitizeOwnTests(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

function save(key: string | null, tests: OwnTest[]): void {
  if (key === null) return;
  try {
    if (tests.length === 0) window.sessionStorage.removeItem(STORAGE_PREFIX + key);
    else window.sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(tests));
  } catch {
    // Storage refused: the cases still hold for this page load.
  }
}

/** The own cases of ONE question, kept for the tab under `key` (an attempt +
 *  position on the real test). They survive Next / Back and a reload, and end
 *  with the tab. `null` = not kept at all (nothing to key them by yet). */
export function useOwnTests(key: string | null): [OwnTest[], (next: OwnTest[]) => void] {
  const [tests, setTests] = useState<OwnTest[]>(() => load(key));
  // The same component can move to another question without remounting
  // (Practice, the recruiter's Preview): follow the key.
  useEffect(() => {
    setTests(load(key));
  }, [key]);
  const update = useCallback(
    (next: OwnTest[]) => {
      const capped = next.slice(0, MAX_OWN_TESTS);
      setTests(capped);
      save(key, capped);
    },
    [key],
  );
  return [tests, update];
}

// ── what the fields mean, per kind of question ─────────────────────────────

export interface OwnTestCopy {
  inputLabel: string;
  inputPlaceholder: string;
  expectedLabel: string;
  expectedPlaceholder: string;
  /** One line under the card's title. */
  hint: string;
}

export function ownTestCopy(language: Language, contract: QuestionContract): OwnTestCopy {
  const examples = authoringFor(language, contract === "typed").editorTestPlaceholders;
  if (contract === "sql") {
    return {
      inputLabel: "Extra SQL before your query (optional)",
      inputPlaceholder: "INSERT INTO … VALUES (…);",
      expectedLabel: "Expected rows (optional)",
      expectedPlaceholder: examples.expected,
      hint: "Each test starts from the database above, runs your extra SQL, then your query. Leave both fields empty to just see the rows your query returns.",
    };
  }
  if (contract === "program") {
    return {
      inputLabel: "Standard input",
      inputPlaceholder: examples.input,
      expectedLabel: "Expected output (optional)",
      expectedPlaceholder: examples.expected,
      hint: "The input is everything your program reads. Leave the expected output empty to just see what it prints.",
    };
  }
  if (contract === "typed") {
    return {
      inputLabel: "Arguments",
      inputPlaceholder: examples.input,
      expectedLabel: "Returns (optional)",
      expectedPlaceholder: examples.expected,
      hint: "Write the arguments the way the example tests do. Leave the expected value empty to just see what your method returns.",
    };
  }
  return {
    inputLabel: "Input",
    inputPlaceholder: examples.input,
    expectedLabel: "Expected (optional)",
    expectedPlaceholder: examples.expected,
    hint: "Write the input the way the example tests do. Leave the expected value empty to just see what your function returns.",
  };
}

/** A function is called WITH something: a blank input is a row not filled in
 *  yet, not a case. SQL and a program run fine on nothing at all. */
function needsInput(contract: QuestionContract): boolean {
  return contract === "function" || contract === "typed";
}

export const OWN_TEST_NO_INPUT_NOTE = "Add an input to run this test.";

// ── the Run, in the browser ────────────────────────────────────────────────

// With nothing expected the engines still want SOMETHING well-formed to
// compare with (a blank JavaScript / SQL expected value is reported as the
// TEST's mistake). The verdict against this stand-in is thrown away.
function standInExpected(language: Language): string {
  if (isJsContract(language)) return "undefined";
  if (language === "sql") return "[]";
  return "";
}

/** Run the candidate's own cases in the tab, in order. Never throws: a case
 *  that cannot run is a row with an `error`. */
export async function runOwnTestsInBrowser(
  language: Language,
  args: {
    code: string;
    functionName: string;
    setupCode?: string | null;
    contract: QuestionContract;
    tests: OwnTest[];
  },
): Promise<OwnTestResult[]> {
  const out: OwnTestResult[] = [];
  for (const t of args.tests) {
    if (needsInput(args.contract) && !t.input.trim()) {
      out.push({ input: t.input, passed: null, note: OWN_TEST_NO_INPUT_NOTE });
      continue;
    }
    const expectsNothing = !t.expected.trim();
    try {
      const r = await runUserCodeAgainstTest(language, {
        code: args.code,
        testInput: t.input,
        expectedOutput: expectsNothing ? standInExpected(language) : t.expected,
        functionName: args.functionName,
        setupCode: args.setupCode,
      });
      if (r.input_error) {
        out.push({
          input: t.input,
          passed: false,
          // SQL: the engine names the statement that failed and why — the
          // authoring hint would only describe a recruiter's test. Python's
          // `error` is a parser traceback, so the literal hint reads better.
          error:
            language === "sql"
              ? `Your extra SQL failed. ${r.error ?? ""}`.trim()
              : `This input could not be read. ${authoringFor(language, false).literalHint}`,
        });
      } else if (r.error) {
        out.push({ input: t.input, passed: false, output: r.output || undefined, error: r.error });
      } else {
        out.push({
          input: t.input,
          passed: expectsNothing ? null : r.passed,
          output: r.output,
        });
      }
    } catch (e) {
      out.push({
        input: t.input,
        passed: false,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  }
  return out;
}

// ── the Run, on our servers ────────────────────────────────────────────────

export interface OwnTestWire {
  input: string;
  expected_output?: string;
}

/** The cases as `/run` takes them: the expected output only when one was typed. */
export function ownTestsPayload(tests: OwnTest[]): OwnTestWire[] {
  return tests.slice(0, MAX_OWN_TESTS).map((t) => ({
    input: t.input,
    ...(t.expected.trim() ? { expected_output: t.expected } : {}),
  }));
}

/** Mirrors `remote_runner.MAX_TESTS_PER_JOB`: the most tests one runner job
 *  (one usage row) takes. */
const RUNNER_JOB_TESTS = 50;

/** Mirrors `remote_runner.own_room`: own cases only fill the room left in the
 *  example tests' LAST job — they never cost a job (a usage row) of their own. */
export function ownRoom(examples: number): number {
  if (examples <= 0) return RUNNER_JOB_TESTS;
  return (RUNNER_JOB_TESTS - (examples % RUNNER_JOB_TESTS)) % RUNNER_JOB_TESTS;
}

/** The row of an own case that did not fit (`remote_runner.OWN_NO_ROOM_NOTE`). */
export const OWN_TEST_NO_ROOM_NOTE =
  "There is no room for it: this question's example tests already fill a run.";

/** The recruiter's Preview has no candidate Run door: its own cases go through
 *  the recruiter's run-code door as ordinary tests, AFTER the example tests —
 *  only as many as fit in the example tests' job (`ownRoom`), as on the
 *  candidate's door. That door compares every test, so a case with nothing
 *  expected comes back "failed" (a program) or "the expected value can't be
 *  read" (typed, where an output is present only when the method returned).
 *  Read those two as what they are here: the case ran. */
export function recruiterOwnTests(
  tests: OwnTest[],
  examples: number,
): Array<{ name: string; input: string; expected_output: string }> {
  return tests.slice(0, Math.min(MAX_OWN_TESTS, ownRoom(examples))).map((t, i) => ({
    name: `Your test ${i + 1}`,
    input: t.input,
    expected_output: t.expected,
  }));
}

export function attachRecruiterOwnResults(
  sent: OwnTest[],
  rows: Array<{ passed: boolean; output?: string; error?: string }>,
  contract: QuestionContract,
  examples: number,
): OwnTestResult[] {
  const fit = Math.min(MAX_OWN_TESTS, ownRoom(examples));
  const ranRows = rows.slice(0, Math.min(sent.length, fit)).map((r, i) => {
    const ran =
      !sent[i].expected.trim() && (!r.error || (contract === "typed" && Boolean(r.output)));
    return {
      input: sent[i].input,
      passed: ran ? null : r.passed,
      ...(r.output ? { output: r.output } : {}),
      ...(r.error && !ran ? { error: r.error } : {}),
    };
  });
  const noRoom = sent
    .slice(fit, MAX_OWN_TESTS)
    .map((t) => ({ input: t.input, passed: null, note: OWN_TEST_NO_ROOM_NOTE }));
  return [...ranRows, ...noRoom];
}

/** The server's rows (in the order the cases were sent) with the inputs they
 *  ran with. A missing row — an older server — is simply not shown. */
export function attachOwnResults(
  sent: OwnTest[],
  rows: Array<{
    passed?: boolean | null;
    output?: string | null;
    error?: string | null;
    note?: string | null;
  }>,
): OwnTestResult[] {
  return rows.slice(0, sent.length).map((r, i) => ({
    input: sent[i].input,
    passed: r.passed ?? null,
    ...(r.output ? { output: r.output } : {}),
    ...(r.error ? { error: r.error } : {}),
    ...(r.note ? { note: r.note } : {}),
  }));
}
